import { cn } from '@/lib/utils';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import { buildDiffCardClass } from '@/components/smartai/ComponentBuildDiff/build-diff-layout';
import {
  callGraphCanvasClass,
  callGraphLayoutClass,
  callGraphLegendClass,
  callGraphRootClass,
  callGraphSearchWrapClass,
  callGraphToolbarClass,
} from './call-graph-layout';

const SkeletonBar = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'animate-pulse rounded-md bg-[var(--inv-surface-muted)]',
      className,
    )}
  />
);

const DarkSkeletonBar = ({ className }: { className?: string }) => (
  <div className={cn('animate-pulse rounded-md bg-white/10', className)} />
);

export const CallGraphSourceSkeleton = () => (
  <div className="flex flex-1 flex-col gap-2 overflow-hidden py-1">
    {Array.from({ length: 12 }).map((_, index) => (
      <SkeletonBar
        key={index}
        className={cn(
          'h-3',
          index % 4 === 0 ? 'w-[92%]' : index % 3 === 0 ? 'w-[76%]' : 'w-[58%]',
        )}
      />
    ))}
  </div>
);

export const CallGraphVisualizationSkeleton = ({
  embedded = true,
}: {
  embedded?: boolean;
}) => (
  <div
    className={cn(callGraphRootClass(embedded), 'animate-pulse')}
    aria-busy="true"
    aria-label="Loading call graph">
    <div className={callGraphToolbarClass}>
      {Array.from({ length: 3 }).map((_, index) => (
        <SkeletonBar key={index} className="h-7 w-24 rounded-full" />
      ))}
      <SkeletonBar className="ml-auto h-3 w-36 max-[900px]:ml-0 max-[900px]:w-full" />
    </div>

    <div className={callGraphLayoutClass}>
      <div className={cn(callGraphCanvasClass(false), 'relative')}>
        <div className={callGraphLegendClass}>
          <DarkSkeletonBar className="mb-1 h-7 w-full rounded-md" />
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center gap-2">
              <DarkSkeletonBar className="h-2.5 w-2.5 shrink-0 rounded-full" />
              <DarkSkeletonBar className="h-3 w-16" />
            </div>
          ))}
        </div>

        <div className={callGraphSearchWrapClass}>
          <DarkSkeletonBar className="h-9 w-full rounded-[10px]" />
        </div>

        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="relative h-[min(18rem,55%)] w-[min(28rem,85%)]">
            {[
              { top: '12%', left: '18%', size: 'h-4 w-4' },
              { top: '28%', left: '52%', size: 'h-3.5 w-3.5' },
              { top: '22%', left: '78%', size: 'h-5 w-5' },
              { top: '48%', left: '32%', size: 'h-5 w-5' },
              { top: '56%', left: '62%', size: 'h-3.5 w-3.5' },
              { top: '68%', left: '24%', size: 'h-3 w-3' },
              { top: '72%', left: '70%', size: 'h-4 w-4' },
            ].map((node, index) => (
              <div
                key={index}
                className="absolute"
                style={{ top: node.top, left: node.left }}>
                <DarkSkeletonBar className={cn('rounded-full', node.size)} />
              </div>
            ))}
            {[
              { top: '20%', left: '30%', width: 'w-24', rotate: 'rotate-[24deg]' },
              { top: '38%', left: '44%', width: 'w-20', rotate: 'rotate-[-18deg]' },
              { top: '52%', left: '38%', width: 'w-28', rotate: 'rotate-[8deg]' },
              { top: '62%', left: '48%', width: 'w-16', rotate: 'rotate-[-32deg]' },
            ].map((edge, index) => (
              <div
                key={`edge-${index}`}
                className="absolute"
                style={{ top: edge.top, left: edge.left }}>
                <DarkSkeletonBar
                  className={cn(
                    'h-0.5 origin-left opacity-40',
                    edge.width,
                    edge.rotate,
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const callGraphCardClass = cn(
  buildDiffCardClass,
  'flex h-[min(72vh,760px)] max-h-[min(72vh,760px)] min-h-0 flex-col overflow-hidden p-0 sm:p-0',
);

const CallGraphSkeleton = () => (
  <div
    className={cn(onboardingPageClass, 'animate-pulse')}
    aria-busy="true"
    aria-label="Loading call graph">
    <header className="mb-5 max-w-[42rem]">
      <SkeletonBar className="mb-3 h-2.5 w-24" />
      <SkeletonBar className="mb-2 h-8 w-[min(100%,10rem)]" />
      <SkeletonBar className="h-4 w-[min(100%,30rem)]" />
    </header>

    <SkeletonBar className="mb-4 h-11 w-full rounded-xl" />

    <section className={callGraphCardClass} aria-hidden="true">
      <CallGraphVisualizationSkeleton embedded />
    </section>
  </div>
);

export default CallGraphSkeleton;
